/**
 * PARTNER SYNC — ONE LIST.
 *
 * The awaiting-review queue is the real workflow: a partner's offer arrives, an
 * operator looks at the margin, somebody approves or declines, a car goes to an
 * airport. For a while there was a second screen beside it listing THE SAME
 * OFFERS with the policy's reasoning on them, so a row here said a booking was
 * waiting and said nothing about why, and finding out meant going and matching
 * references by eye in a second table. That is the thing this page replaces:
 * each card now carries its own decision, and the standalone table is deleted
 * rather than left to disagree with this one.
 *
 * What did NOT merge in, and why:
 *
 *   the counters    they count offers that have ALREADY LEFT this queue
 *                   (auto-approved today, held, overridden in 30 days), which
 *                   a list of unresolved offers structurally cannot — so they
 *                   are a strip above the list rather than a second screen.
 *   the drill-down  a rule trace, the AI evidence, the post-approval chain:
 *                   a page of one offer, not a list of offers. Linked from
 *                   every card.
 *   the log         history, including resolved and auto-approved offers this
 *                   queue never shows. A different question, kept.
 *   the filters     they belong to the log. This page's "filter" is the queue:
 *                   everything on it is waiting.
 *
 * APPROVE AND DECLINE ARE UNTOUCHED. They still go through OrderInputModal and
 * ./syncQueue's one `postSyncDecision` — the order form is where the price and
 * the operator get set, and the decisions endpoint is read-only.
 */

import React, { useEffect, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import PartnersService from '../../Services/PartnersService';
import Error from '../../Components/Error';
import OrderInputModal from '../Orders/Components/OrderInputModal';
import { formatBookingDate, formatDateTime, DATE_FORMATS } from '../../Utils/timezone';
// Reading the queue and the one call that approves or declines an offer. They
// live in ./syncQueue because the auto-approval drill-down's "Approve anyway"
// and "Reject" have to be THIS action, not a second copy of it.
import {
  DECLINE_CONFIRMATION, SYNC_FLOW, fetchSyncQueue, postSyncDecision,
} from './syncQueue';
// The profit thresholds, the money formatter and the single × 100 that turns a
// margin fraction into a percentage moved to ./presentation when the
// auto-approval decision view started showing the same three numbers. Same
// values, same behaviour — one copy.
import {
  formatMarginPct,
  formatMoneyOrDash,
  isAbsent,
  prettifyToken,
  profitToneFor,
} from './presentation';
import {
  QUEUE_COUNTER_WINDOW_DAYS,
  decisionLookup,
  fetchQueueDecisionContext,
  marginForCard,
} from './queueDecisions';
import { heldBreakdown, newestDecisionAt, scoredTotals } from './AutoApproval/decisionFacts';
import { windowLabel } from './AutoApproval/useDecisionFeed';
import CounterCards from './Components/CounterCards';
import QueueDecision from './Components/QueueDecision';
import RunDecisionPass from './Components/RunDecisionPass';

// Map a raw backend status onto a readable label + a bootstrap badge tone.
const statusPresentation = (status) => {
  switch (status) {
    case 'FAILED':
      return { label: 'Failed', bg: 'danger' };
    case 'PENDING_CANCELLED':
      return { label: 'Pending cancel', bg: 'secondary' };
    case 'PENDING':
      return { label: 'Pending review', bg: 'warning', text: 'dark' };
    default:
      return { label: prettifyToken(status), bg: 'light', text: 'dark' };
  }
};

/**
 * What we know about the policy's decisions before the first read comes back:
 * nothing. `decisionLookup()` with no page answers "unknown" for every
 * reference — not "nothing was scored", which would be a claim about the
 * policy made out of our own ignorance.
 */
const NO_DECISIONS = {
  lookup: decisionLookup(),
  rows: [],
  summary: null,
  disagreements: null,
};

function PartnerSync() {
  const [partnerBookings, setPartnerBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [decisions, setDecisions] = useState(NO_DECISIONS);
  const [showOrderInputModal, setShowOrderInputModal] = useState(false);
  const [originalOrder, setOriginalOrder] = useState({});
  const [error, setError] = useState('');
  const [stateHash, setStateHash] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const fetchPartners = async () => {
    try {
      const fetchedPartners = await PartnersService.fetchPartners();
      setPartners(fetchedPartners?.data?.data);
    } catch (err) {
      setError(error);
    }
  };

  const handleClickOnView = (order = {}, hash = '') => {
    setOriginalOrder(order);
    setStateHash(hash);

    setTimeout(() => {
      setShowOrderInputModal(true);
    }, 100);
  };

  const findPartner = (partnerUuid) => {
    const partner = partners.find((entry) => entry.Id === partnerUuid);
    // Be defensive — a DDB row may carry a partner UUID that no longer
    // matches any Partners row (placeholder during partner onboarding,
    // deleted partner, etc.). Don't crash the page.
    return partner?.name ?? '(Unknown partner)';
  };

  /**
   * The queue, and then the decisions about it.
   *
   * IN THAT ORDER, AND THE SECOND CANNOT BREAK THE FIRST. The queue is the
   * screen — a partner booking still has to be approved on an afternoon when
   * Mongo is unhappy — so the decision read is awaited after the rows are set
   * and its failure degrades the cards rather than the page. The window it asks
   * for is derived from the rows themselves (see ./queueDecisions), which is
   * why it has to happen here and not in parallel.
   */
  const fetchPartnerBookings = async () => {
    setIsLoading(true);
    try {
      const queue = await fetchSyncQueue();
      setPartnerBookings(queue);
      setDecisions(await fetchQueueDecisionContext(queue));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const processPartnerBooking = async () => {
    setPartnerBookings([]);

    try {
      await postSyncDecision({
        order: originalOrder,
        flow: SYNC_FLOW.APPROVED,
        stateHash,
      });
      await fetchPartnerBookings();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeclinePartnerBooking = async (order = {}, hash = '') => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(DECLINE_CONFIRMATION)) {
      return; // Exit the function if the user cancels
    }

    setPartnerBookings([]);

    try {
      await postSyncDecision({ order, flow: SYNC_FLOW.DECLINED, stateHash: hash });
      await fetchPartnerBookings();
    } catch (err) {
      setError(err.message);
    }
  };

  const debouncedFetchPartnerBookings = debounce(async () => {
    fetchPartnerBookings();
  }, 500);

  const debouncedFetchPartners = debounce(async () => {
    fetchPartners();
  }, 500);

  useEffect(() => {
    debouncedFetchPartnerBookings();
    debouncedFetchPartners();

    return () => {
      debouncedFetchPartnerBookings.cancel();
      debouncedFetchPartners.cancel();
    };
  }, []);

  const bookingCount = partnerBookings.length;
  const showEmptyState = !isLoading && hasLoadedOnce && bookingCount === 0;
  const totals = scoredTotals(decisions.summary?.byDecision);
  const held = heldBreakdown(decisions.summary?.byHeldReason);
  // NOT "last run": a pass that scored nothing writes no record, so the newest
  // decision is the only readable thing and it is labelled as what it is. It
  // earns its place on this page because it is the answer to "why does that
  // card say the policy has not looked at it yet".
  const newest = newestDecisionAt(decisions.rows);

  return (
    <div className="partner-sync">
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Partner
              {' '}
              <span className="hero-highlight">Sync</span>
            </h1>
            <p className="lead text-muted mb-4">
              Every incoming partner booking that is still waiting, with what the
              auto-approval policy made of it. Check the margin, read the hold,
              approve or decline — in one place.
            </p>
            <div className="hero-actions d-flex align-items-center gap-3 flex-wrap">
              <Button onClick={fetchPartnerBookings} size="lg" disabled={isLoading}>
                <i className={`fa fa-sync ${isLoading ? 'fa-spin' : ''} me-2`} />
                {isLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
              {/* Beside Refresh, because the pair is "read it again" and
                  "score it again" and this is the screen that shows what the
                  scoring produced — including the "Not scored yet" cards that
                  are the reason somebody reaches for it. Its own note says the
                  results arrive asynchronously; Refresh is what collects
                  them. */}
              <RunDecisionPass onError={setError} />
              {/* Real <Link>s, not `Button as={Link}`: react-bootstrap's Button
                  puts role="button" on the anchor, which tells a screen reader
                  the control acts here rather than navigating.
                  Both go to screens this queue is NOT a copy of — the log is
                  history (resolved and auto-approved offers are not on this
                  page at all), and the policy screen is the rules themselves. */}
              <Link to="/partner-sync/auto-approval/log" className="btn btn-outline-primary btn-lg">
                <i className="fa fa-clipboard-list me-2" />
                Decision log
              </Link>
              <Link to="/partner-sync/auto-approval/policy" className="btn btn-outline-secondary btn-lg">
                <i className="fa fa-scale-balanced me-2" />
                Policy
              </Link>
              {!isLoading && hasLoadedOnce && bookingCount > 0 && (
                <span className="sync-summary">
                  <strong>{bookingCount}</strong>
                  {' '}
                  {bookingCount === 1 ? 'booking' : 'bookings'}
                  {' '}
                  awaiting review
                </span>
              )}
              {newest && (
                <span className="sync-summary sync-summary--muted">
                  {`Policy's last decision ${formatDateTime(newest, DATE_FORMATS.TIME_ONLY)}`}
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-rotate fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      {/* The three counts this queue cannot contain. Above the list because
          they are the context for it, not a second list of it. */}
      <CounterCards
        totals={totals}
        held={held}
        disagreements={decisions.disagreements}
        period={windowLabel(QUEUE_COUNTER_WINDOW_DAYS)}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="text-center my-5 py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3 mb-0">Loading partner bookings…</p>
        </div>
      )}

      {/* Empty State */}
      {showEmptyState && (
        <div className="sync-empty">
          <div className="sync-empty-icon">
            <i className="fa fa-circle-check" />
          </div>
          <h4 className="fw-bold mb-2">You&apos;re all caught up</h4>
          <p className="text-muted mb-4">
            There are no partner bookings waiting to be synced right now.
          </p>
          <Button variant="outline-primary" onClick={fetchPartnerBookings}>
            <i className="fa fa-sync me-2" />
            Check again
          </Button>
        </div>
      )}

      {/* Card Grid */}
      {!isLoading && bookingCount > 0 && (
        <div className="sync-grid">
          {partnerBookings.map((booking) => {
            const isFailed = booking.Status === 'FAILED';
            const status = statusPresentation(booking.Status);
            const { state, decision } = decisions.lookup(booking.BookingId);
            // ONE margin per card, from the sync row's own analysis or — when
            // pricing failed at ingestion and the policy managed it later —
            // from the decision's features. Same quantity, same scale; see
            // marginForCard. `profitToneFor` checks absence itself, so an
            // offer nobody could price is MUTED rather than toned as a loss
            // beside the dash it renders.
            const margin = marginForCard(booking, decision);
            const profitTone = profitToneFor(margin.pct);

            return (
              <div
                key={booking.BookingId}
                className={`sync-card${isFailed ? ' is-failed' : ''}`}
              >
                <div className="sync-card-head">
                  <div className="sync-card-head-main">
                    <div className="sync-partner">{findPartner(booking.PartnerId)}</div>
                    <div className="sync-ref">
                      #
                      {booking.BookingId}
                    </div>
                  </div>
                  <Badge bg={status.bg} text={status.text} className="sync-status">
                    {status.label}
                  </Badge>
                </div>

                <div className="sync-route">
                  <div className="sync-route-point">
                    <span className="sync-route-label">From</span>
                    <span className="sync-route-value">
                      {booking.Data.destination_from.address}
                    </span>
                  </div>
                  <i className="fa fa-arrow-right-long sync-route-arrow" />
                  <div className="sync-route-point">
                    <span className="sync-route-label">To</span>
                    <span className="sync-route-value">
                      {booking.Data.destination_to.address}
                    </span>
                  </div>
                </div>

                <div className="sync-meta">
                  <div className="sync-meta-item">
                    <span className="sync-meta-label">Transfer</span>
                    <span className="sync-meta-value">
                      {booking.Data.date_datetime
                        ? formatBookingDate(booking.Data)
                        : 'Incorrect date'}
                    </span>
                  </div>
                  <div className="sync-meta-item">
                    <span className="sync-meta-label">Passenger</span>
                    <span className="sync-meta-value">{booking.Data.full_name}</span>
                  </div>
                  <div className="sync-meta-item">
                    <span className="sync-meta-label">Phone</span>
                    <span className="sync-meta-value">{booking.Data.phone_number}</span>
                  </div>
                  <div className="sync-meta-item">
                    <span className="sync-meta-label">Passengers</span>
                    <span className="sync-meta-value">{booking.Data.passengers.amount}</span>
                  </div>
                  <div className="sync-meta-item">
                    <span className="sync-meta-label">Vehicle</span>
                    <span className="sync-meta-value">
                      {booking.Data.category?.name || '—'}
                    </span>
                  </div>
                  <div className="sync-meta-item">
                    <span className="sync-meta-label">Flight</span>
                    <span className="sync-meta-value">
                      {booking.Data.flight_number || '—'}
                    </span>
                  </div>
                </div>

                {/* Why this one is waiting — the whole point of the merge. */}
                <QueueDecision
                  state={state}
                  decision={decision}
                  reference={booking.BookingId}
                />

                <div className="sync-card-foot">
                  <div className="sync-price-block">
                    <span className="sync-price">
                      {booking.Data.price}
                      {' '}
                      {booking.Data.currency}
                    </span>
                    {/* The cue is rendered whenever a margin was MEASURED. The
                        trend arrow used to be chosen by `profit_pct >= 0`,
                        which is `true` for a null — an up-arrow on a margin
                        nobody measured — so the whole block is gated on
                        presence and the dash case never reaches it. */}
                    {!isAbsent(margin.pct) && (
                      <span
                        className={`sync-profit sync-profit--${profitTone}`}
                        title={`Partner offer ${formatMoneyOrDash(margin.offer, margin.offerCurrency)}`}
                      >
                        <i
                          className={`fa ${Number(margin.pct) >= 0 ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'}`}
                        />
                        {formatMarginPct(margin.pct)}
                        <span className="sync-profit-ours">
                          {`Our ${formatMoneyOrDash(margin.our, margin.ourCurrency)}`}
                        </span>
                      </span>
                    )}
                  </div>

                  {isFailed ? (
                    <p className="sync-failed-note mb-0">
                      <i className="fa fa-triangle-exclamation me-2" />
                      Scraper failed after multiple retries — handle this booking
                      manually on the partner&apos;s portal.
                    </p>
                  ) : (
                    <div className="sync-actions">
                      <Button
                        variant="outline-danger"
                        disabled={booking.Status === 'PENDING_CANCELLED'}
                        onClick={() => handleDeclinePartnerBooking(
                          booking.Data,
                          booking.Metadata.state_hash,
                        )}
                      >
                        Decline
                      </Button>
                      <Button
                        onClick={() => handleClickOnView(
                          booking.Data,
                          booking.Metadata.state_hash,
                        )}
                      >
                        <i className="fa fa-eye me-2" />
                        Review
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showOrderInputModal ? (
        <OrderInputModal
          show={showOrderInputModal}
          handleClose={() => setShowOrderInputModal(false)}
          originalOrder={originalOrder}
          fetchOrders={processPartnerBooking}
        />
      ) : null}
    </div>
  );
}

export default PartnerSync;
